import seedrandom from 'seedrandom';

import type { Answer } from '@/types/respondent';
import type { Element, ElementContext, Field, Instruction, Item, Step, StepInfo, Survey } from '@/types/survey';

/**
 * When a step's `isRandomized` property is true, randomize the order of its items using a specified random seed.
 *
 * @param step - The survey step whose item order may be randomized.
 * @param randomSeed - The respondent's integer seed used to randomize the item order.
 * @returns A new Step object identical to the original, but with a randomized item order if applicable.
 */
function applyRandomSeedToStep(step: Step, randomSeed: number): Step {
  // If randomization is not enabled, return the step unchanged.
  if (!step.isRandomized) return step;

  const randomNumberGenerator = seedrandom(String(randomSeed));
  const shuffledItems: Item[] = [...step.items].sort(() => randomNumberGenerator() - 0.5);
  return { ...step, items: shuffledItems };
}

/**
 * For a given survey, randomize the order of items within all steps that have the `isRandomized` property set to true.
 *
 * @param randomSeed - The respondent's random seed used to shuffle item order within steps.
 * @param survey - The survey to randomize.
 * @returns A new Survey object identical to the original but with modified Step objects.
 */
export function applyRandomSeedToSurvey(randomSeed: number | undefined, survey: Survey): Survey {
  // Skip if no random seed or if there are no steps to process
  if (!randomSeed || !survey?.steps?.length) return survey;

  const stepsAfterItemsShuffled: Step[] = survey.steps.map((step: Step) =>
    applyRandomSeedToStep(step, randomSeed)
  );
  return { ...survey, steps: stepsAfterItemsShuffled };
}

/**
 * Throws an error for values that should be impossible.
 *
 * Used to enforce exhaustive type checking in discriminated unions.
 * If a value reaches this function, it indicates a missing case.
 *
 * @param x - A value that should have type `never` if all union cases are handled correctly.
 * @param message - Optional error message to include in the thrown Error.
 * @throws {Error} Always throws with the provided message.
 */
export function assertNever(x: never, message = 'Unexpected value'): never {
  throw new Error(message);
}

/**
 * Assigns page boundaries for all elements in the given range [lo...hi].
 * No-ops if the range is empty or out of order.
 */
export function closePage(
  contexts: ElementContext[],
  lo: number,
  hi: number
): void {
  if (lo > hi) return;
  const min = Math.max(0, lo);
  const max = Math.min(hi, contexts.length - 1);
  for (let k = min; k <= max; k++) {
    contexts[k].pageStart = min;
    contexts[k].pageEnd = max;
  }
}

/**
 * Assigns step boundaries for all elements in the given range [lo...hi].
 *
 * @param contexts - The array of element navigation contexts.
 * @param lo - Index of the first element in the step segment.
 * @param hi - Index of the last element in the step segment.
 * @param stepId - The ID of the step these elements belong to.
 * @param lastAnswerableIndexInStep - Index of the last element in the step that can be answered.
 */
export function closeStep(
  contexts: ElementContext[],
  lo: number,
  hi: number,
  stepId: string,
  lastAnswerableIndexInStep: number | null
): void {
  if (lo > hi) return;
  const min = Math.max(0, lo);
  const max = Math.min(hi, contexts.length - 1);
  for (let k = min; k <= max; k++) {
    contexts[k].stepStart = min;
    contexts[k].stepEnd = max;
    contexts[k].step = stepId;
    contexts[k].lastAnswerableIndexInStep = lastAnswerableIndexInStep;
  }
}

/**
 * Flattens a survey's steps into a single list of elements in render order.
 *
 * @param steps - The ordered list of survey steps.
 * @returns A flat array of elements (instructions, items, fields) in administration order.
 */
export function createFlatMapOfElements(steps: Step[]): Element[] {
  return steps.flatMap(s => [
    ...(s.instructions ?? []),
    ...(s.items ?? []),
    ...(s.fields ?? []),
  ]);
}

/**
 * Creates a flat list of step IDs aligned 1:1 with the survey's flat element list.
 *
 * Each element position in the returned array corresponds to the step that element belongs to.
 *
 * @param steps - The ordered list of survey steps.
 * @returns A flat array of step IDs aligned with the flattened element list.
 */
export function createFlatMapOfStepIds(steps: Step[]): string[] {
  return steps.flatMap(s =>
    Array((
      (s.instructions?.length ?? 0) +
      (s.items?.length ?? 0) +
      (s.fields?.length ?? 0)
    )).fill(s.id)
  )
}

/**
 * Build a map of element IDs → context metadata for each element in a survey.
 *
 * Contexts include:
 *   - element ID, index, and step ID
 *   - pageStart/pageEnd based on `isAfterPageBreak`
 *   - stepStart/stepEnd based on `stepIds`
 *
 * @param elements - Flat list of survey elements in render order.
 * @param stepIds - Flat list of step IDs aligned 1:1 with the elements.
 * @returns Map keyed by element ID with its ElementContext as the value.
 */
export function createElementContextMap(
  elements: Element[],
  stepIds: string[]
): Map<string, ElementContext> {
  const elementCount: number = elements.length;

  // Preallocate contexts aligned to indices of `elements`
  const contexts: ElementContext[] = Array.from({ length: elementCount }, () => ({
    id: '',
    index: 0,
    step: '',
    pageStart: 0,
    pageEnd: 0,
    stepStart: 0,
    stepEnd: 0,
    lastAnswerableIndexInStep: null,
  }));
  // Running segment starts
  let pageStart = 0;
  let stepStart = 0;

  for (let i = 0; i < elementCount; i++) {
    const el = elements[i];
    contexts[i].id = el.id;
    contexts[i].index = i;

    const stepChanged: boolean = i > 0 && stepIds[i] !== stepIds[i - 1];

    if (stepChanged) {
      const lastAnswerableIndexInStep: number | null = findLastAnswerableElement(elements, stepStart, i - 1);

      // STEP BOUNDARY: close the *current page first*, then the step.
      closePage(contexts, pageStart, i - 1);
      closeStep(contexts, stepStart, i - 1, stepIds[i - 1], lastAnswerableIndexInStep);

      // New step starts a new page as well
      stepStart = i;
      pageStart = i;
    } else if (el.isAfterPageBreak && i > 0) {
      // PAGE BOUNDARY within the *same* step
      closePage(contexts, pageStart, i - 1);
      pageStart = i;
    }
  }

  // Close trailing segments for the final page/step
  if (elementCount > 0) {
    closePage(contexts, pageStart, elementCount - 1);
    const lastAnswerableIndexInStep: number | null = findLastAnswerableElement(elements, stepStart, elementCount - 1);
    closeStep(contexts, stepStart, elementCount - 1, stepIds[elementCount - 1], lastAnswerableIndexInStep);
  }

  return new Map<string, ElementContext>(contexts.map(c => [c.id, c]));
}

/**
 * Build a map of step IDs → step information for each step in a survey.
 *
 * Info includes a lightweight Step object minus properties of items, instructions, fields, and isRandomized.
 *
 * @param steps - List of steps in the survey.
 * @returns Map keyed by step ID with its StepInfo as the value.
 */
export function createStepMap(steps: Step[]): Map<string, StepInfo> {
  return new Map(
    steps.map(({ items, instructions, fields, isRandomized, ...rest }: Step): [string, StepInfo] => [
      rest.id,
      {
        ...rest,
        instructionCount: instructions.length,
        answerableElementIds: new Set([...items, ...fields].map((element: Item | Field) => element.id))
      }
    ])
  );
}

/**
 * Returns the index of the last answerable element in a step range, or null if none.
 *
 * "Answerable" includes:
 * - elements with a `groups` property (Fields)
 * - elements with an `optionSet` property (Items)
 */
export function findLastAnswerableElement(
  elements: Element[],
  stepStart: number,
  stepEnd: number
): number | null {
  if (stepStart < 0 || stepEnd < 0) return null;
  if (stepStart >= elements.length) return null;

  const end = Math.min(stepEnd, elements.length - 1);
  const start = Math.max(stepStart, 0);

  if (start > end) return null;

  for (let i = end; i >= start; i--) {
    if (isAnswerableElement(elements[i])) return i;
  }

  return null;
}

/**
 * Get the ID of a step at a given index within a survey.
 *
 * @param survey - The Survey to read from
 * @param index - The step index (e.g., 0 for first, -1 for last)
 * @returns The step ID at that position, or null if unavailable
 */
export function getStepIdAtIndex(
  survey: Survey,
  index: number
): string | null {
  return survey.steps.at(index)?.id ?? null;
}

/**
 * When enabled, move unanswered items to the end of the step while preserving relative item order.
 *
 * @param step - The survey step whose items may be regrouped.
 * @param answerMap - Map of item IDs to answers, with the most recent answer first.
 * @returns A new Step object with answered items first and unanswered items last, if enabled.
 */
export function groupUnansweredItemsAtEnd(step: Step, answerMap: Map<string, Answer[]>): Step {
  if (!step.shouldGroupUnansweredItems) return step;

  const answeredItems: Item[] = [];
  const unansweredItems: Item[] = [];

  for (const item of step.items) {
    const latestAnswer: Answer | undefined = answerMap.get(item.id)?.[0];
    const hasAnswer = Boolean(
      latestAnswer &&
      !latestAnswer.deletedAt &&
      (latestAnswer.options?.length > 0 || latestAnswer.value)
    );

    if (hasAnswer) {
      answeredItems.push(item);
    } else {
      unansweredItems.push(item);
    }
  }

  return {
    ...step,
    items: [...answeredItems, ...unansweredItems],
  };
}

/**
 * Apply unanswered-item grouping to every step in a survey.
 *
 * @param survey - The survey whose steps may be regrouped.
 * @param answerMap - Map of item IDs to answers, with the most recent answer first.
 * @returns A new Survey object with regrouped steps where enabled.
 */
export function groupUnansweredItemsAtEndOfSteps(
  survey: Survey,
  answerMap: Map<string, Answer[]>
): Survey {
  return {
    ...survey,
    steps: survey.steps.map((step: Step) =>
      groupUnansweredItemsAtEnd(step, answerMap)
    ),
  };
}

/**
 * Check whether an element is answerable.
 * Answerable elements have either a `groups` property (Fields) or `optionSet` property (Items).
 */
function isAnswerableElement(el: Element): boolean {
  // Item: has optionSet (string | null)
  if ('optionSet' in el) return true;

  // Field: has groups (array)
  return 'groups' in el && Array.isArray((el as any).groups);
}

/**
 * Type guard that determines whether an element is an Instruction.
 *
 * @param el - The element to evaluate.
 * @returns True if the element has the structural properties of an Instruction.
 */
export function isInstruction(el: Element): el is Instruction {
  return 'body' in el;
}

/**
 * Type guard that determines whether an element is an Item.
 *
 * @param el - The element to evaluate.
 * @returns True if the element has the structural properties of an Item.
 */
export function isItem(el: Element): el is Item {
  return 'optionSet' in el;
}

/**
 * Type guard that determines whether an element is a Field.
 *
 * @param el - The element to evaluate.
 * @returns True if the element has the structural properties of a Field.
 */
export function isField(el: Element): el is Field {
  return 'category' in el;
}

/**
 * For a given step, remove any fields related to request a login code be sent via email.
 * Also removes fields for verifying ownership of an email address.
 *
 * @param step - The survey step from which fields are being filtered.
 * @returns A new Step object identical to the original, but with login code email request fields and email address
 *        verification fields removed.
 */
function removeLoginCodeEmailRequestFieldsFromStep(step: Step): Step {
  const filteredFields: Field[] = step.fields.filter((field: Field) => !['LER', 'EAV', 'AUT'].includes(field.category));
  return { ...step, fields: filteredFields };
}

/**
 * For a given survey, remove any fields related to requesting a login code be sent via email
 * (also removes email address verification fields).
 *
 * @param survey - The survey from which to remove login code email request fields.
 * @returns A new Survey object identical to the original, but with updated Step objects.
 */
export function removeLoginCodeEmailRequestFieldsFromSurvey(survey: Survey): Survey {
  const modifiedSteps: Step[] = survey.steps.map(removeLoginCodeEmailRequestFieldsFromStep);
  return { ...survey, steps: modifiedSteps };
}